import { Button } from "components/DesignSystem/Button/Button";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import { CrossIcon } from "components/icons/CrossIcon";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import { formatDate } from "utils/formatDate";
import { OverviewEntity } from "./brandOverview.types";
import { EMPTY_FIELD } from "./constants";
import { EntityStatesOfRegistration } from "./EntityStatesOfRegistration";
import { LinkedDocField } from "./LinkedDocField";
import { PanelField } from "./PanelField";

type EntityOverviewPanelProps = {
  entity: OverviewEntity;
  onClose: () => void;
  onViewDetails: (entityId: string) => void;
};

// data-attribute name matchers for the file-backed fields
const EIN_DOC_NAME = "ein_letter_docs";
const COI_DOC_NAME = "certificate_of_incorporation";
const BUSINESS_ACTIVITY_NAME = "business_activity";
const TAX_YEAR_END_NAME = "tax_year";

const EIN_DOC_LABEL = "EIN Letter or Form SS-4";
const COI_DOC_LABEL_DISPLAY = "Certificate of Incorporation";

const toFileIds = (value: unknown): string[] =>
  Array.isArray(value) ? (value as string[]) : [];

const linkedDocTooltip = (docName: string) => (
  <div className="t-text-left">
    <span className="t-text-neutral-30">Linked document:</span>
    <br />
    {docName}
  </div>
);

export const EntityOverviewPanel = ({
  entity,
  onClose,
  onViewDetails,
}: EntityOverviewPanelProps) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const { data: attributes, isLoading } = useGetEntityDataAttributesQuery(
    { groupId, entityId: entity.id },
    { skip: !groupId || !entity.id, refetchOnMountOrArgChange: true }
  );

  const einFileIds = toFileIds(
    attributes?.find((attr) => attr.name?.includes(EIN_DOC_NAME))?.value
  );
  const coiFileIds = toFileIds(
    attributes?.find((attr) => attr.name?.includes(COI_DOC_NAME))?.value
  );

  const attrText = (name: string): string => {
    const value = attributes?.find((attr) => attr.name === name)?.value;
    return typeof value === "string" ? value : "";
  };

  const rawDateOfIncorporation =
    attrText("date_of_incorporation") || entity.dateOfIncorporation;
  const dateOfIncorporation = rawDateOfIncorporation
    ? formatDate(rawDateOfIncorporation)
    : "";
  const businessActivity = attrText(BUSINESS_ACTIVITY_NAME);

  const taxYearEndAttribute = attributes?.find(
    (attr) => attr.name === TAX_YEAR_END_NAME
  );
  const taxYearEnd = taxYearEndAttribute?.options.find(
    (option) => option.value === String(taxYearEndAttribute.value)
  )?.label;

  const handleViewDetails = () => onViewDetails(entity.id);

  return (
    <div className="t-flex t-h-full t-flex-1 t-flex-col t-gap-5">
      <div className="t-flex t-min-w-0 t-flex-col t-gap-3">
        <div className="t-flex t-items-start t-justify-between t-gap-3">
          <span className="t-text-caption t-font-light t-uppercase t-tracking-wide t-text-neutral-50">
            Entity overview
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to company overview"
            className="all:unset t--mr-1.5 t--my-1.5 t-flex t-shrink-0 t-cursor-pointer t-items-center t-justify-center t-rounded-md t-p-1.5 t-text-text-30 t-transition-colors hover:t-bg-neutral-0 hover:t-text-text-60"
          >
            <CrossIcon className="t-h-4 t-w-4" />
          </button>
        </div>
        <span className="t-truncate t-text-subtext t-text-text-100">
          {entity.name}
        </span>
      </div>

      <div className="t-grid t-grid-cols-2 t-gap-x-6 t-gap-y-5">
        <PanelField
          label="Date of incorporation"
          tooltip={linkedDocTooltip(COI_DOC_LABEL_DISPLAY)}
        >
          {isLoading ? (
            <Skeleton className="t-mt-1.5 t-h-4 t-w-40 t-rounded-full" />
          ) : (
            <LinkedDocField
              value={dateOfIncorporation || EMPTY_FIELD}
              docLabel={COI_DOC_LABEL_DISPLAY}
              fileIds={coiFileIds}
            />
          )}
        </PanelField>
        <PanelField label="Entity type">
          {entity.entityTypeLabel || EMPTY_FIELD}
        </PanelField>
        <PanelField
          label="EIN number"
          tooltip={linkedDocTooltip(EIN_DOC_LABEL)}
        >
          <LinkedDocField
            value={entity.ein || EMPTY_FIELD}
            docLabel={EIN_DOC_LABEL}
            fileIds={einFileIds}
          />
        </PanelField>
        <PanelField label="Major business activity">
          {isLoading ? (
            <Skeleton className="t-mt-1.5 t-h-4 t-w-40 t-rounded-full" />
          ) : (
            businessActivity || EMPTY_FIELD
          )}
        </PanelField>
        <PanelField label="Tax Year End">
          {isLoading ? (
            <Skeleton className="t-mt-1.5 t-h-4 t-w-40 t-rounded-full" />
          ) : (
            taxYearEnd || EMPTY_FIELD
          )}
        </PanelField>
      </div>

      <PanelField label="States of registration">
        <EntityStatesOfRegistration
          entityId={entity.id}
          states={entity.states}
        />
      </PanelField>

      <Button
        customType="secondary"
        block
        onClick={handleViewDetails}
        rightIcon={<SmallRightArrow />}
        className="t-mt-auto"
      >
        View entity details
      </Button>
    </div>
  );
};
